import React, { useState } from 'react';
import { Button, Table, Tag, Select, Modal, message as messageApi } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_APP_VERSION_LIST, GetAppVersionListResponse, AppVersionItem } from './graphql/queries/getAppVersionList';
import { CREATE_APP_VERSION } from './graphql/mutations/createAppVersion';
import { UPDATE_APP_VERSION } from './graphql/mutations/updateAppVersion';
import { DELETE_APP_VERSION } from './graphql/mutations/deleteAppVersion';
import AppVersionFormDrawer from './components/AppVersionFormDrawer';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { showError } from 'utils/exceptionUtils';
import dayjs from 'dayjs';

const APP_TYPE_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'PDA', value: 'pda' },
    { label: 'Android', value: 'mobile_android' },
    { label: 'iOS', value: 'mobile_ios' },
];

const AppVersionPage: React.FC = () => {
    const { t } = useTranslation();
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
    const [selectedVersion, setSelectedVersion] = useState<AppVersionItem | null>(null);

    const { data, loading, refetch } = useQuery<GetAppVersionListResponse>(GET_APP_VERSION_LIST, {
        variables: {
            type: typeFilter || null,
            page: currentPage,
            size: pageSize,
        },
        fetchPolicy: 'network-only',
    });

    const [createAppVersion, { loading: creating }] = useMutation(CREATE_APP_VERSION);
    const [updateAppVersion, { loading: updating }] = useMutation(UPDATE_APP_VERSION);
    const [deleteAppVersion] = useMutation(DELETE_APP_VERSION);

    const versionList = data?.getAppVersionList?.content || [];
    const totalElements = data?.getAppVersionList?.totalElements || 0;

    const handleCreate = () => {
        setSelectedVersion(null);
        setDrawerMode('create');
        setDrawerVisible(true);
    };

    const handleEdit = (record: AppVersionItem) => {
        setSelectedVersion(record);
        setDrawerMode('edit');
        setDrawerVisible(true);
    };

    const handleDelete = (record: AppVersionItem) => {
        Modal.confirm({
            title: t('central.appVersion.messages.deleteConfirm'),
            onOk: async () => {
                try {
                    await deleteAppVersion({ variables: { id: record.id } });
                    messageApi.success(t('central.appVersion.messages.deleteSuccess'));
                    refetch();
                } catch (error: any) {
                    showError(error);
                }
            },
        });
    };

    const handleSubmit = async (values: any) => {
        try {
            if (drawerMode === 'create') {
                await createAppVersion({ variables: values });
                messageApi.success(t('central.appVersion.messages.createSuccess'));
            } else {
                await updateAppVersion({
                    variables: {
                        id: values.id,
                        type: values.type,
                        versionCode: values.versionCode,
                        versionName: values.versionName,
                        url: values.url,
                        isForceUpdate: values.isForceUpdate,
                        forceUpdateVersionCode: values.forceUpdateVersionCode,
                        enabledAt: values.enabledAt,
                        description: values.description,
                    },
                });
                messageApi.success(t('central.appVersion.messages.updateSuccess'));
            }
            setDrawerVisible(false);
            refetch();
        } catch (error: any) {
            showError(error);
        }
    };

    const typeLabel = (type: string) => {
        const map: Record<string, string> = {
            pda: 'PDA',
            mobile_android: 'Android',
            mobile_ios: 'iOS',
        };
        return map[type] || type;
    };

    const columns: TableColumnsType<AppVersionItem> = [
        {
            title: t('central.appVersion.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (type: string) => <Tag>{typeLabel(type)}</Tag>,
        },
        {
            title: t('central.appVersion.columns.versionName'),
            dataIndex: 'versionName',
            key: 'versionName',
            width: 100,
        },
        {
            title: t('central.appVersion.columns.versionCode'),
            dataIndex: 'versionCode',
            key: 'versionCode',
            width: 100,
        },
        {
            title: t('central.appVersion.columns.isForceUpdate'),
            dataIndex: 'isForceUpdate',
            key: 'isForceUpdate',
            width: 120,
            render: (val: boolean) => (
                <Tag color={val ? 'red' : 'default'}>
                    {val ? t('central.appVersion.forceUpdate.yes') : t('central.appVersion.forceUpdate.no')}
                </Tag>
            ),
        },
        {
            title: t('central.appVersion.columns.forceUpdateVersionCode'),
            dataIndex: 'forceUpdateVersionCode',
            key: 'forceUpdateVersionCode',
            width: 120,
            render: (val: number | null) => val ?? '-',
        },
        {
            title: t('central.appVersion.columns.url'),
            dataIndex: 'url',
            key: 'url',
            width: 160,
            render: (text: string) => (
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }} title={text}>
                    {text}
                </div>
            ),
        },
        {
            title: t('central.appVersion.columns.enabledAt'),
            dataIndex: 'enabledAt',
            key: 'enabledAt',
            width: 170,
            render: (val: string | null) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: t('central.appVersion.columns.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: (val: string | null) => val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: t('central.appVersion.columns.actions'),
            key: 'actions',
            width: 150,
            render: (_: any, record: AppVersionItem) => (
                <div className="flex gap-2">
                    <Button type="link" size="small" onClick={() => handleEdit(record)}>
                        {t('components.button.edit')}
                    </Button>
                    <Button type="link" size="small" danger onClick={() => handleDelete(record)}>
                        {t('components.button.delete')}
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="flex justify-between items-center gap-2 mb-3 py-2">
                <div className="flex items-center gap-2">
                    <span>{t('central.appVersion.columns.type')}:</span>
                    <Select
                        size="middle"
                        style={{ width: 160 }}
                        value={typeFilter}
                        onChange={(val) => {
                            setTypeFilter(val);
                            setCurrentPage(1);
                        }}
                        options={APP_TYPE_OPTIONS}
                    />
                </div>
                <Button type="primary" onClick={handleCreate}>
                    <span className="font-bold">{t('components.button.new')}</span>
                </Button>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={versionList}
                loading={loading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                tableLayout="auto"
                className="mb-4"
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== pageSize) setPageSize(size);
                }}
                onShowSizeChange={(_, size) => {
                    setCurrentPage(1);
                    setPageSize(size);
                }}
                pageSizeOptions={['10', '20', '50', '100']}
            />

            <AppVersionFormDrawer
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                onSubmit={handleSubmit}
                appVersion={selectedVersion}
                mode={drawerMode}
                loading={creating || updating}
            />
        </div>
    );
};

export default AppVersionPage;
